import React, { useState } from "react";
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";
import {
  MiTable,
  MiCollapse,
  MiIconButton,
  MiIcon,
  MiMenu,
  MiButton,
} from "@miview/components";
import { accountingTransactionService } from "@miview/api";
import { createToast } from "@miview/toast";
import {
  TOAST_TYPES,
  ACCOUNTINGTRANSACTIONSTATUSES,
  ACCOUNTPAYABLE_MENU_ITEMS,
  HTTP_STATUSES,
} from "@miview/constants";
import { mdiChevronDown, mdiClose, mdiFormatListBulleted } from "@mdi/js";
import { useComponentState, usePayPeriods } from "@miview/hooks";
import { TransactionPaidModal } from "./Modals/TransactionPaidModal";
import { getFilename, openFileSaveDialog } from "@miview/utils";

const useStyles = makeStyles()((theme) => {
  return {
    payBillsButtonContainer: {
      backgroundColor: theme.palette.light.teal,
      display: "flex",
      padding: "5px",
    },
    payBillsTitleContainer: {
      color: theme.palette.primary.white,
      backgroundColor: theme.palette.primary.main,
      fontWeight: theme.fontWeight.bold,
      padding: "10px",
    },
    payBillsTableContainer: {
      height: "100%",
      minHeight: "100vh",
      width: "350px",
      display: "flex",
      flexDirection: "column",
      background: "white",
    },
    updateBtn: {
      borderRadius: "4px",
      borderWidth: "1px",
      width: "155px",
    },
    printBtn: {
      borderRadius: "4px",
      borderWidth: "1px",
      flex: 1,
    },
  };
});

export const PayBillsCollapse = (props) => {
  const {
    selectedRows,
    setSelectedRows,
    removeFromDrawer,
    removeAllFromDrawer,
    fetchData,
    formatCurrency,
  } = props;

  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [shouldCreateImport, setShouldCreateImport] = useState(true);

  const { payPeriods, selectedPayPeriod, setSelectedPayPeriod } =
    usePayPeriods();
  const theme = useTheme();
  const stateManager = useComponentState();
  const { classes } = useStyles();

  const handlePaidStatus = () => {
    stateManager.run(async () => {
      const payload = {
        status: ACCOUNTINGTRANSACTIONSTATUSES.PAID,
        payDate: selectedPayPeriod,
      };
      try {
        const actions = selectedRows.map((accountingTransaction) =>
          accountingTransactionService.update({
            id: accountingTransaction.id,
            request: payload,
          })
        );
        const results = await Promise.allSettled(actions);
        if (results.every((res) => res.value.status === HTTP_STATUSES.OK)) {
          createToast("Status Update Success", TOAST_TYPES.SUCCESS);
          if (shouldCreateImport) {
            await downloadQuickBooksImport();
          }
        }
      } finally {
        closePayModal();
        setSelectedRows([]);
        fetchData();
      }
    });
  };

  const downloadQuickBooksImport = async () => {
    const transactionIds = selectedRows.map((e) => e.id);
    const response = await accountingTransactionService.exportQuickBooksIif(
      transactionIds
    );
    if (response.status === HTTP_STATUSES.OK) {
      const filename = getFilename(response.headers["content-disposition"]);
      openFileSaveDialog(response.data, filename);
      setShouldCreateImport(true);
    }
  };

  const getIdByURL = (url) => {
    return url.substring(url.lastIndexOf("/") + 1);
  };

  const getFailuresAndSuccess = (results) => {
    const successfullIds = [];
    const unsuccessfullIds = [];
    results.forEach((result) => {
      const id = getIdByURL(result.value.request.responseURL);
      if (result.value.status === HTTP_STATUSES.OK) {
        successfullIds.push(id);
      } else {
        unsuccessfullIds.push(id);
      }
    });
    return { successfullIds, unsuccessfullIds };
  };

  const handleUpdateStatus = async (value) => {
    const payload = { status: value };
    if (value === ACCOUNTINGTRANSACTIONSTATUSES.PAID) {
      openPayModal();
      return;
    }
    try {
      const actions = selectedRows.map((obj) =>
        accountingTransactionService.update({
          id: obj.id,
          request: payload,
        })
      );

      const results = await Promise.allSettled(actions);

      const { successfullIds, unsuccessfullIds } =
        getFailuresAndSuccess(results);

      const successString = successfullIds.length
        ? `Accounting Transactions ${successfullIds} statuses successfully updated. `
        : "";
      const failureString = unsuccessfullIds.length
        ? `Accounting Transactions ${unsuccessfullIds} status could not be updated. `
        : "";
      const message = `${failureString} ${successString}`;
      let type = TOAST_TYPES.SUCCESS;

      if (unsuccessfullIds.length) {
        type = TOAST_TYPES.ERROR;
      }
      createToast(message, type, {
        autoClose: 10000,
        pauseOnHover: true,
      });
    } finally {
      setSelectedRows([]);
      fetchData();
    }
  };

  const handleClearAllButtonClick = () => {
    if (selectedRows.length === 0) {
      createToast("No Entries Selected", TOAST_TYPES.ERROR);
      return;
    }
    setSelectedRows([]);
  };

  const handlePrintButtonClick = () => {
    let myWindow = window.open(" ", " ", "width=1800,height=1200");
    myWindow.document.write(
      '<html><head><title> </title><link rel="stylesheet" type="text/css" href="styles.css"></head><body>'
    );
    myWindow.document.write(document.getElementById("printcls").innerHTML);
    myWindow.document.write("</body></html>");
    myWindow.document.close();
    myWindow.focus();
    myWindow.print();
    myWindow.close();
  };

  const getClassName = (params) => {
    if (
      !params.row?.id &&
      params?.id &&
      params?.id.includes("auto-generated")
    ) {
      return "groupedRow";
    }
    return "";
  };

  const openPayModal = () => {
    setIsPayModalOpen(true);
  };

  const closePayModal = () => {
    setIsPayModalOpen(false);
    setSelectedPayPeriod("");
  };

  const getGroupedVals = (params) => {
    return params.value;
  };

  const drawerColumns = [
    {
      field: "payableToUserName",
      leafField: "address",
      hide: true,
      headerName: "Assigned",
      groupingValueGetter: (params) => getGroupedVals(params),
    },
    { field: "address", headerName: "Address", hide: true },
    {
      field: "amount",
      headerName: "Amount",
      headerAlign: "left",
      align: "left",
      sortable: false,
      renderCell: ({ value }) => {
        if (!value) {
          return null;
        }
        return (
          <div style={{ fontWeight: theme.fontWeight.bold }}>
            {formatCurrency(value)}
          </div>
        );
      },
    },
    {
      field: "clear",
      renderHeader: () => (
        <MiIcon
          path={mdiClose}
          color={"red"}
          onClick={handleClearAllButtonClick}
        />
      ),
      sortable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "left",
      renderCell: (param) => {
        return (
          <MiIconButton
            onClick={() =>
              Object.keys(param.row).length
                ? removeFromDrawer(param.row)
                : removeAllFromDrawer(param.rowNode)
            }
            style={{ cursor: "pointer" }}
          >
            <MiIcon path={mdiClose} color={"red"} />
          </MiIconButton>
        );
      },
    },
  ];

  const isButtonDisabled = !selectedRows?.length || stateManager.isBusy();

  const renderButtons = () => {
    return (
      <>
        <div className={classes.payBillsTitleContainer}>Pay Bills</div>
        <div className={classes.payBillsButtonContainer}>
          <MiMenu
            data={ACCOUNTPAYABLE_MENU_ITEMS}
            selectedColor={theme.palette.primary.teal}
            disabled={isButtonDisabled}
            handleChange={handleUpdateStatus}
            OptionalElement={
              <MiButton
                title="Update"
                color={theme.palette.primary.teal}
                className={classes.updateBtn}
                disabled={isButtonDisabled}
                icon={mdiChevronDown}
              />
            }
          />
          <MiButton
            key={2}
            title="Print"
            color={theme.palette.primary.teal}
            className={classes.printBtn}
            onClick={handlePrintButtonClick}
          />
        </div>
      </>
    );
  };

  return (
    <>
      <TransactionPaidModal
        open={isPayModalOpen}
        onClose={closePayModal}
        onSubmit={handlePaidStatus}
        selectedPayPeriod={selectedPayPeriod}
        setSelectedPayPeriod={setSelectedPayPeriod}
        shouldCreateImport={shouldCreateImport}
        setShouldCreateImport={setShouldCreateImport}
        payPeriods={payPeriods}
      />
      <MiCollapse in={true} orientation={"horizontal"}>
        <div className={classes.payBillsTableContainer}>
          {renderButtons()}
          <MiTable
            columns={drawerColumns}
            rows={selectedRows}
            disableColumnPinning={true}
            defaultGroupingExpansionDepth={-1}
            rowGroupingColumnMode="single"
            toolbarProps={{ visible: false }}
            columnVisibilityModel={{
              address: false,
              payableToUserName: false,
            }}
            initialState={{
              rowGrouping: {
                model: ["payableToUserName"],
              },
            }}
            groupingColDef={{
              leafField: "address",
            }}
            getRowClassName={(params) => getClassName(params)}
            onRowGroupingModelChange={() => {
              return;
            }}
            experimentalFeatures={{
              rowGrouping: true,
            }}
            emptyListProps={{
              icon: mdiFormatListBulleted,
              text: "There are no records to display",
            }}
          />
        </div>
      </MiCollapse>
    </>
  );
};
